import MessageListView from "issara-sdk/apis/MessageListView_apps_MSG";
import MessageTemplateList from "issara-sdk/apis/MessageTemplateList_apps_PRX";
import PatientAppointmentView from "issara-sdk/apis/PatientAppointmentView_apps_QUE";
import MessageTemplateSerializer from "issara-sdk/types/MessageTemplateSerializer_apps_PRX";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { type ChatMessage } from "../../patient/teleHealthModelV4";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  LOAD: "LOAD",
  LOAD_MESSAGES: "LOAD_MESSAGES",
} as const;

export const SEQUENCE_NAME = "TeleHealthV4" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  TeleHealthV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    appointments: PatientAppointmentSerializer[];
    templates: MessageTemplateSerializer[];
    messages: ChatMessage[];
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "chatDetail">;

export type Event = { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.LOAD]: { patientId: number; divisionId?: number };
  [ACTIONS.LOAD_MESSAGES]: { chatChannelId: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  TeleHealthV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      TeleHealthV4Sequence: {
        ...state.TeleHealthV4Sequence,
        sequenceIndex: "Action",
      },
    },
    () => {
      if ("action" in params) {
        Action(controller, params as unknown as ActionType);
      }
    },
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.LOAD]: HandleLoad,
    [ACTIONS.LOAD_MESSAGES]: HandleLoadMessages,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleLoad: Handler<Params<Actions["LOAD"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    TeleHealthV4Sequence: { ...state.TeleHealthV4Sequence, appointments: [], messages: undefined },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.LOAD]: null },
  });

  const common = {
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  };

  const [[appointments, appointmentError], [templates, templateError]]: ApiResult[] =
    await Promise.all([
      PatientAppointmentView.list({
        ...common,
        params: { patient_id: params.patientId, division_id: params.divisionId, limit: 50 },
      }),
      MessageTemplateList.list({ ...common, params: { limit: 99 } }),
    ]);

  if (appointmentError || templateError) {
    SetErrorMessage(controller, {
      ...params,
      btnAction: BTN_ACTS.LOAD,
      error: appointmentError || templateError,
    });

    return;
  }

  state = controller.getState();

  controller.setState({
    TeleHealthV4Sequence: {
      ...state.TeleHealthV4Sequence,
      appointments: appointments?.items ?? [],
      templates: templates?.items ?? [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD]: BLC_STATUS.SUCCESS },
  });
};

const HandleLoadMessages: Handler<Params<Actions["LOAD_MESSAGES"]>> = async (
  controller,
  params,
) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_MESSAGES]: BLC_STATUS.LOADING },
  });

  const [res, error]: ApiResult = await MessageListView.list({
    chat_channel_id: params.chatChannelId,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.LOAD_MESSAGES, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    TeleHealthV4Sequence: { ...state.TeleHealthV4Sequence, messages: res?.items ?? [] },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_MESSAGES]: BLC_STATUS.SUCCESS },
  });
};
